<script setup lang="ts">
import type { KeyDef } from '../types';

withDefaults(
  defineProps<{
    keys: KeyDef[];
    columns?: number;
    stretch?: boolean;
  }>(),
  { columns: 4, stretch: false },
);

const emit = defineEmits<{ press: [key: KeyDef] }>();
</script>

<template>
  <div
    class="win-keypad"
    :class="{ stretch }"
    :style="{ gridTemplateColumns: `repeat(${columns}, 1fr)` }"
  >
    <button
      v-for="(k, i) in keys"
      :key="i"
      type="button"
      class="win-key"
      :class="[k.class, { wide: k.span && k.span > 1 }]"
      :style="k.span && k.span > 1 ? { gridColumn: `span ${k.span}` } : undefined"
      @click="emit('press', k)"
    >
      <svg
        v-if="k.icon === 'backspace'"
        class="key-icon backspace"
        viewBox="0 0 24 16"
        aria-hidden="true"
      >
        <path
          fill="none"
          stroke="currentColor"
          stroke-width="1.2"
          stroke-linejoin="round"
          d="M21.5 2.5H9.2L2.5 8l6.7 5.5h12.3V2.5z"
        />
      </svg>
      <span v-else>{{ k.label }}</span>
    </button>
  </div>
</template>

<style scoped>
.win-keypad {
  display: grid;
  gap: var(--win-gap);
  padding: 2px 4px 4px;
  background: var(--win-surface);
  flex: 0 0 auto;
}

.win-keypad.stretch {
  flex: 1;
  min-height: 0;
  grid-auto-rows: 1fr;
}

.win-key {
  min-height: 40px;
  border-radius: var(--win-radius-key);
  font-size: 15px;
  font-weight: 400;
  background: var(--win-key-num);
  border: 1px solid var(--win-border);
  color: var(--win-text);
}

.win-keypad.stretch .win-key {
  min-height: 0;
  height: 100%;
}

.win-key:hover {
  background: var(--win-key-num-hover);
}

.win-key:active {
  background: var(--win-key-num-press);
}

.win-key.mem,
.win-key.fn {
  background: var(--win-key-fn);
  font-size: 12px;
  font-weight: 400;
  color: var(--win-text-secondary);
}

.win-key.mem:hover,
.win-key.fn:hover {
  background: var(--win-key-fn-hover);
  color: var(--win-text);
}

.win-key.op {
  background: var(--win-key-op);
  font-size: 16px;
}

.win-key.op:hover {
  background: var(--win-key-op-hover);
}

.win-key.equals {
  background: var(--win-key-equals);
  color: var(--win-key-equals-text);
  font-size: 18px;
}

.win-key.equals:hover {
  background: var(--win-key-equals-hover);
}

.win-key .key-icon {
  display: block;
  width: 22px;
  height: 14px;
  margin: 0 auto;
}

.win-key .key-icon.backspace {
  color: var(--win-text-secondary);
}
</style>
